import { type FC, useEffect } from 'react'

import { Box, Title, WizardStepRenderParams } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import {
  AssignmentForm,
  type AssignmentFormData,
} from 'modules/it_management/components/AssignmentForm'
import { useAssignmentFormSaveDisabled } from 'modules/it_management/hooks/useAssignmentFormSaveDisabled'

import { WizardData } from './types'

const AssignmentStep: FC<WizardStepRenderParams<WizardData>> = ({ data, onDataChanged }) => {
  const formData: AssignmentFormData = {
    assignmentType: data.assignmentType ?? 'person',
    selectedEmployeeId: data.employeeId ?? undefined,
    selectedWorkplace:
      data.workplaceId && data.workplaceName
        ? {
            id: data.workplaceId,
            name: data.workplaceName,
          }
        : null,
    selectedTeam:
      data.teamId && data.teamName
        ? {
            id: data.teamId,
            name: data.teamName,
          }
        : null,
    selectedLocation:
      data.locationId && data.locationName
        ? {
            id: data.locationId,
            name: data.locationName,
          }
        : null,
    deviceStatus: data.deviceStatus ?? 'in_stock',
  }

  const isSaveDisabled = useAssignmentFormSaveDisabled(data.assignmentType ?? 'person')

  // Update wizard data when disabled state changes
  useEffect(() => {
    if (data.isSaveDisabled !== isSaveDisabled) {
      onDataChanged({
        ...data,
        isSaveDisabled,
      })
    }
  }, [isSaveDisabled, data, onDataChanged])

  const handleFormChange = (newFormData: AssignmentFormData) => {
    const updates: Partial<WizardData> = {
      assignmentType: newFormData.assignmentType,
      employeeId: undefined,
      workplaceId: undefined,
      workplaceName: undefined,
      teamId: undefined,
      teamName: undefined,
      locationId: undefined,
      locationName: undefined,
      deviceStatus: newFormData.deviceStatus,
    }

    switch (newFormData.assignmentType) {
      case 'person':
        updates.employeeId = newFormData.selectedEmployeeId ?? null
        break
      case 'workplace':
        updates.workplaceId = newFormData.selectedWorkplace?.id
        updates.workplaceName = newFormData.selectedWorkplace?.name
        break
      case 'team':
        updates.teamId = newFormData.selectedTeam?.id
        updates.teamName = newFormData.selectedTeam?.name
        break
      case 'space':
        updates.locationId = newFormData.selectedLocation?.id
        updates.locationName = newFormData.selectedLocation?.name
        break
      case 'none':
        updates.deviceStatus = newFormData.deviceStatus ?? 'in_stock'
        break
    }

    onDataChanged({
      ...data,
      ...updates,
    })
  }

  return (
    <Box gap="s32" maxWidth="s400">
      <Title
        description={i18n.t('it_management.assignment_step.description')}
        title={i18n.t('it_management.assignment_step.title')}
      />
      <Box gap="s16" paddingRight="s24">
        <AssignmentForm value={formData} onChange={handleFormChange} showOnlyInStock />
      </Box>
    </Box>
  )
}

export default AssignmentStep
